/**
 * Authentication Service
 * Handles user authentication using AWS Cognito via Amplify
 */

import { 
  signIn, 
  signOut, 
  signUp, 
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes,
  signInWithRedirect
} from 'aws-amplify/auth';

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} Sign in result
 */
export const signInWithEmail = async (email, password) => {
  try {
    const result = await signIn({
      username: email,
      password
    });
    return result;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

/**
 * Sign in with social provider (Google or Facebook)
 * @param {string} provider - Provider name ('Google' or 'Facebook')
 * @returns {Promise<void>}
 */
export const signInWithSocial = async (provider) => {
  try {
    await signInWithRedirect({
      provider
    });
  } catch (error) {
    console.error(`Error signing in with ${provider}:`, error);
    throw error;
  }
};

/**
 * Sign out current user
 * @returns {Promise<void>}
 */
export const signOutUser = async () => {
  try {
    await signOut();
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Sign up new user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {Object} attributes - Additional user attributes
 * @returns {Promise<Object>} Sign up result
 */
export const signUpWithEmail = async (email, password, attributes = {}) => {
  try {
    const result = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          ...attributes
        }
      }
    });
    return result;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
};

/**
 * Confirm sign up with verification code
 * @param {string} email - User email
 * @param {string} code - Verification code
 * @returns {Promise<Object>} Confirmation result
 */
export const confirmSignUpCode = async (email, code) => {
  try {
    const result = await confirmSignUp({
      username: email,
      confirmationCode: code
    });
    return result;
  } catch (error) {
    console.error('Error confirming sign up:', error);
    throw error;
  }
};

/**
 * Resend verification code
 * @param {string} email - User email
 * @returns {Promise<Object>} Resend result
 */
export const resendVerificationCode = async (email) => {
  try {
    const result = await resendSignUpCode({
      username: email
    });
    return result;
  } catch (error) {
    console.error('Error resending verification code:', error);
    throw error;
  }
};

/**
 * Initiate password reset
 * @param {string} email - User email
 * @returns {Promise<Object>} Reset result
 */
export const initiatePasswordReset = async (email) => {
  try {
    const result = await resetPassword({
      username: email
    });
    return result;
  } catch (error) {
    console.error('Error initiating password reset:', error);
    throw error;
  }
};

/**
 * Confirm password reset with code
 * @param {string} email - User email
 * @param {string} code - Verification code
 * @param {string} newPassword - New password
 * @returns {Promise<void>}
 */
export const confirmPasswordReset = async (email, code, newPassword) => {
  try {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword
    });
  } catch (error) {
    console.error('Error confirming password reset:', error);
    throw error;
  }
};

/**
 * Get current authenticated user
 * @returns {Promise<Object|null>} Current user or null
 */
export const getAuthenticatedUser = async () => {
  try {
    const user = await getCurrentUser();
    return user;
  } catch (error) {
    // User not authenticated
    return null;
  }
};

/**
 * Get current user's JWT tokens
 * @returns {Promise<Object>} Auth session with tokens
 */
export const getAuthSession = async () => {
  try {
    const session = await fetchAuthSession();
    return session;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    throw error;
  }
};

/**
 * Get JWT access token for API requests
 * @returns {Promise<string|null>} JWT access token or null
 */
export const getAccessToken = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() || null;
  } catch (error) {
    console.error('Error getting access token:', error);
    return null;
  }
};

/**
 * Get JWT ID token (contains user claims)
 * @returns {Promise<string|null>} JWT ID token or null
 */
export const getIdToken = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
};

/**
 * Get user attributes from Cognito
 * @returns {Promise<Object>} User attributes
 */
export const getUserAttributes = async () => {
  try {
    const attributes = await fetchUserAttributes();
    return attributes;
  } catch (error) {
    console.error('Error fetching user attributes:', error);
    throw error;
  }
};

/**
 * Update user attributes
 * @param {Object} attributes - Attributes to update
 * @returns {Promise<Object>} Update result
 */
export const updateUserProfile = async (attributes) => {
  try {
    const result = await updateUserAttributes({
      userAttributes: attributes
    });
    return result;
  } catch (error) {
    console.error('Error updating user attributes:', error);
    throw error;
  }
};

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>} True if authenticated
 */
export const isAuthenticated = async () => {
  try {
    await getCurrentUser();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get user roles from token claims
 * @returns {Promise<Array<string>>} User roles
 */
export const getUserRoles = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    
    if (!idToken) {
      return [];
    }

    const payload = idToken.payload;
    const rolesStr = payload['custom:roles'] || '';
    return rolesStr.split(',').map(role => role.trim()).filter(Boolean);
  } catch (error) {
    console.error('Error getting user roles:', error);
    return [];
  }
};

/**
 * Check if user has specific role
 * @param {string} role - Role to check
 * @returns {Promise<boolean>} True if user has role
 */
export const hasRole = async (role) => {
  try {
    const roles = await getUserRoles();
    return roles.includes(role);
  } catch (error) {
    return false;
  }
};

/**
 * Check if user is administrator
 * @returns {Promise<boolean>} True if user is admin
 */
export const isAdmin = async () => {
  return hasRole('administrator');
};

/**
 * Check if user is speaker
 * @returns {Promise<boolean>} True if user is speaker
 */
export const isSpeaker = async () => {
  return hasRole('speaker');
};

export default {
  signInWithEmail,
  signInWithSocial,
  signOutUser,
  signUpWithEmail,
  confirmSignUpCode,
  resendVerificationCode,
  initiatePasswordReset,
  confirmPasswordReset,
  getAuthenticatedUser,
  getAuthSession,
  getAccessToken,
  getIdToken,
  getUserAttributes,
  updateUserProfile,
  isAuthenticated,
  getUserRoles,
  hasRole,
  isAdmin,
  isSpeaker
};
